import { FC, useCallback, useMemo } from "react";
import { CostType, ProviderType } from "@/services/cloudchipr.api";
import { costTypes } from "@/components/pages/resource-explorer/components/settings-panel/components/cost-type-settings-section/utils/constants";
import { CostTypeSelectItem } from "@/components/pages/resource-explorer/components/settings-panel/components/cost-type-settings-section/CostTypeSelectItem";
import { DropdownSelect } from "@/components/common/select/dropdown-select/DropdownSelect";
import { HierarchySelectDropdownHeader } from "@/components/pages/common/hierarchy-select-visibility/components/HierarchySelectDropdownHeader";
import { CostTypeSelectChipTriggerComponent } from "@/components/pages/resource-explorer/components/settings-panel/components/cost-type-settings-section/trigger-components/CostTypeSelectChipTriggerComponent";
import { FilterTriggerComponentProps } from "@/components/common/select/dropdown-select/utils/types/filterTriggerComponentProps";
import { CostTypeSelectTextFieldTriggerComponent } from "@/components/pages/resource-explorer/components/settings-panel/components/cost-type-settings-section/trigger-components/CostTypeSelectTextFieldTriggerComponent";

interface CostTypeSelectProps {
  costType: CostType;
  onCostTypeChange(costType: CostType): void;
  size?: "small" | "medium";
  workFor?: Extract<ProviderType, "aws" | "azure">;
  variant?: "dropDown" | "select";
}

export const CostTypeSelect: FC<CostTypeSelectProps> = ({
  costType,
  workFor,
  size = "medium",
  onCostTypeChange,
  variant,
}) => {
  const submitHandlerOnClose = useCallback(
    (selectedValues: string[]) => {
      const newCostType = selectedValues[0] as CostType;
      if (costType === newCostType) {
        return;
      }

      onCostTypeChange(newCostType);
    },
    [onCostTypeChange, costType],
  );

  const options = useMemo(() => {
    return Object.entries(costTypes)
      .filter(([_, data]) => !workFor || data.providers.includes(workFor))
      .map(([key, data]) => {
        return {
          value: key,
          label: <CostTypeSelectItem {...data} value={key} workFor={workFor} />,
        };
      });
  }, [workFor]);

  const TriggerComponent = useCallback(
    (props: FilterTriggerComponentProps) => {
      if (variant === "dropDown") {
        return (
          <CostTypeSelectChipTriggerComponent {...props} workFor={workFor} />
        );
      }
      return (
        <CostTypeSelectTextFieldTriggerComponent
          {...props}
          workFor={workFor}
          size={size}
        />
      );
    },
    [workFor, variant, size],
  );

  return (
    <DropdownSelect
      singleSelect
      sortSelectedOptions={false}
      showSearch={false}
      label="Cost Type"
      options={options}
      values={[costType ?? ""]}
      submitHandlerOnClose={submitHandlerOnClose}
      TriggerComponent={TriggerComponent}
      DropdownHeader={HierarchySelectDropdownHeader}
    />
  );
};
